import { useState } from 'react'
import { ArrowRight, CheckCircle } from 'lucide-react'

interface LeadCaptureFormProps {
  onCaptured: (lead: {name: string, email: string, phone: string}) => void
}

export default function LeadCaptureForm({ onCaptured }: LeadCaptureFormProps) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
  })
  const [loading, setLoading] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    // Send lead to webhooks immediately
    try {
      // Send to your API/webhook
      await fetch('https://api.insidethishome.com/capture-lead', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          source: 'landing_page_step1',
          captured_at: new Date().toISOString(),
        }),
      }).catch(() => {
        // Silently fail - we still have the data locally
      })
    } catch (e) {
      // Continue even if webhook fails
    }

    setLoading(false)
    setSubmitted(true)
    
    // Small delay to show success, then proceed
    setTimeout(() => {
      onCaptured(formData)
    }, 800)
  }

  if (submitted) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-8 text-center">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="text-green-600" size={32} />
        </div>
        <h3 className="text-lg font-semibold mb-2">Thanks {formData.name.split(' ')[0]}!</h3>
        <p className="text-gray-600">Loading your pricing options...</p>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-xl shadow-lg p-8">
      <h3 className="text-xl font-bold mb-2">Get Started Free</h3>
      <p className="text-gray-600 mb-6">Enter your info to see pricing and start your trial.</p>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <input
            type="text"
            placeholder="Full Name"
            required
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>
        
        <div>
          <input
            type="email"
            placeholder="Email Address"
            required
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>
        
        <div>
          <input
            type="tel"
            placeholder="Phone Number"
            required
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full py-3 bg-primary text-white rounded-lg hover:bg-primary-dark flex items-center justify-center gap-2 disabled:opacity-50 font-medium"
        >
          {loading ? 'Processing...' : 'See Pricing'}
          <ArrowRight size={18} />
        </button>
      </form>

      <p className="text-xs text-gray-500 text-center mt-4">
        No credit card required to start. Cancel anytime.
      </p>
    </div>
  )
}
